import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const letterValues = {
  A: 1, B: 2, C: 3, D: 4, E: 5,
  F: 6, G: 7, H: 8, I: 9,
  J: 1, K: 2, L: 3, M: 4, N: 5,
  O: 6, P: 7, Q: 8, R: 9,
  S: 1, T: 2, U: 3, V: 4, W: 5,
  X: 6, Y: 7, Z: 8
};

const KarmicLessonNumberCalculator = () => {
  const [fullName, setFullName] = useState('');
  const [karmicLessons, setKarmicLessons] = useState([]);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setFullName(e.target.value);
    setError('');
  };

  const calculateKarmicLessons = () => {
    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    const name = fullName.replace(/\s+/g, '').toUpperCase();

    const presentNumbers = new Set();
    name.split('').forEach(letter => {
      if (letterValues[letter]) {
        presentNumbers.add(letterValues[letter]);
      }
    });

    const missingNumbers = [];
    for (let i = 1; i <= 9; i++) {
      if (!presentNumbers.has(i)) {
        missingNumbers.push(i);
      }
    }

    setKarmicLessons(missingNumbers);

    const totalNumbers = 9;
    const presentCount = presentNumbers.size;
    const missingCount = totalNumbers - presentCount;

    setChartData({
      bar: {
        labels: ['Present Numbers', 'Missing Numbers'],
        datasets: [{
          label: 'Count',
          data: [presentCount, missingCount],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(255, 99, 132, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Present Numbers', 'Missing Numbers'],
        datasets: [{
          data: [presentCount, missingCount],
          backgroundColor: [
            'rgba(75, 192, 192, 0.7)',
            'rgba(255, 99, 132, 0.7)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const getInterpretation = (number) => {
    const interpretations = {
      1: 'Lesson in independence and self-confidence',
      2: 'Lesson in cooperation and diplomacy',
      3: 'Lesson in self-expression and creativity',
      4: 'Lesson in stability and organization',
      5: 'Lesson in freedom and adaptability',
      6: 'Lesson in responsibility and nurturing',
      7: 'Lesson in introspection and spirituality',
      8: 'Lesson in power and material success',
      9: 'Lesson in humanitarianism and compassion'
    };
    return interpretations[number] || '';
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Karmic Lesson Number Calculator | Discover Your Life Lessons</title>
        <meta name="description" content="Calculate your Karmic Lesson Numbers using our Numerology Calculator. Discover the life lessons you need to learn in this lifetime." />
        <meta name="keywords" content="karmic lesson, numerology calculator, life lessons, personal growth" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/karmic-lesson" />
        <meta property="og:title" content="Karmic Lesson Number Calculator | Discover Your Life Lessons" />
        <meta property="og:description" content="Calculate your Karmic Lesson Numbers using our Numerology Calculator. Discover the life lessons you need to learn in this lifetime." />
        <meta property="og:image" content="https://yourwebsite.com/images/karmic-lesson-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/karmic-lesson" />
        <meta property="twitter:title" content="Karmic Lesson Number Calculator | Discover Your Life Lessons" />
        <meta property="twitter:description" content="Calculate your Karmic Lesson Numbers using our Numerology Calculator. Discover the life lessons you need to learn in this lifetime." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/karmic-lesson-og-image.jpg" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">📚</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Karmic Lesson Number Calculator</h1>
            <p className="text-lg text-red-600 font-semibold">Discover Your Life Lessons</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fullName">
            Full Name:
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={fullName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your full name"
          />
        </div>

        <button
          onClick={calculateKarmicLessons}
          className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Karmic Lessons
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {karmicLessons.length > 0 && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Karmic Lessons</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Karmic Lesson Numbers</h4>
                <div className="space-y-2">
                  {karmicLessons.map((number, index) => (
                    <p key={index} className="flex justify-between">
                      <span>Lesson {index + 1}</span>
                      <span className="font-medium">{number}</span>
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Interpretation</h4>
                <div className="space-y-4">
                  {karmicLessons.map((number, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <p className="font-medium">Lesson {number}:</p>
                      <p className="text-gray-700">{getInterpretation(number)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Count'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Category'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Present vs Missing Numbers'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Pie Chart</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Number Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="bg-white p-6 rounded-xl shadow-md mt-8">
              <h4 className="text-xl font-semibold mb-4">Calculation Process</h4>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Step 1: Name Analysis</p>
                  <p className="text-gray-700">Your name was converted to uppercase and spaces were removed: <span className="font-medium">{fullName.replace(/\s+/g, '').toUpperCase()}</span></p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Step 2: Letter to Number Conversion</p>
                  <p className="text-gray-700">Each letter was converted to its corresponding number (A=1, B=2, ..., Z=8)</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Step 3: Missing Numbers Identification</p>
                  <p className="text-gray-700">Numbers from 1 to 9 that were not present in your name were identified as your Karmic Lessons</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">Step 4: Result Interpretation</p>
                  <p className="text-gray-700">Each missing number corresponds to a specific life lesson based on numerology principles</p>
                </div>
              </div>
            </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Karmic Lesson Numbers:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-red-600 mb-4">Step 1: Enter Full Name</h3>
              <p className="text-gray-700">
                Input your complete full name (first, middle, last) in the provided field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-red-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Karmic Lessons" button to process your name.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-red-600 mb-4">Step 3: Review Results</h3>
              <p className="text-gray-700">
                Examine your Karmic Lesson Numbers, their meanings, and visual representations.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-red-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Karmic Lesson Number calculation</li>
                <li>Interactive visualizations with Bar and Pie charts</li>
                <li>Detailed interpretations for each lesson</li>
                <li>Responsive design for all devices</li>
                <li>Step-by-step guidance and explanations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-red-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Gain insights into your personal growth areas</li>
                <li>Understand your life's challenges and lessons</li>
                <li>Make informed decisions about self-improvement</li>
                <li>Enhance self-awareness and personal development</li>
                <li>Discover your soul's journey and purpose</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are Karmic Lesson Numbers?</h3>
              <p className="text-gray-700">
                Karmic Lesson Numbers reveal the life lessons you need to learn in this lifetime based on the missing numbers in your name.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are they calculated?</h3>
              <p className="text-gray-700">
                Each letter in your name corresponds to a number (1-9). The calculator identifies which numbers are missing from your name.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What if I have no Karmic Lessons?</h3>
              <p className="text-gray-700">
                If all numbers are present in your name, it suggests a well-rounded set of experiences in this lifetime.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Can I change my Karmic Lessons?</h3>
              <p className="text-gray-700">
                While you can't change your Karmic Lessons, understanding them can help you work on those specific areas of personal growth.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How accurate is this calculator?</h3>
              <p className="text-gray-700">
                The calculator uses established numerology principles to provide accurate results based on your input.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Should I use my legal name or nickname?</h3>
              <p className="text-gray-700">
                For most accurate results, use your full legal name as it appears on official documents.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KarmicLessonNumberCalculator;